import React, { useEffect, useState, useMemo, useCallback } from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import API, { UPLOADS_URL } from '../api/axios';

const resolveImage = (src, fallback = '') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const Recognition = () => {
  const pageData = usePage('recognition');
  const sections = pageData.sections || {};

  const [awards, setAwards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loadingAwards, setLoadingAwards] = useState(true);

  // Modal / Lightbox state
  const [activeModalIndex, setActiveModalIndex] = useState(null);

  // Fetch awards and categories from backend API
  useEffect(() => {
    setLoadingAwards(true);
    API.get('/awards/public')
      .then((res) => {
        if (res.data?.data) {
          setAwards(res.data.data || []);
        }
        if (res.data?.categories) {
          setCategories(res.data.categories || []);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch awards:', err);
      })
      .finally(() => {
        setLoadingAwards(false);
      });
  }, []);

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || pageData.title || 'Recognition & Accreditations';
  const bannerSubtitle =
    banner.subtitle ||
    'Celebrating Educational Excellence, Institutional Accreditations & Milestones';
  const bannerBreadcrumb = banner.breadcrumb || 'RECOGNITION';
  const bannerImgSrc = pageData.bannerImage || '/images/page-banners/why-choose-us.jpg';

  const honours = sections.honours_section || {};
  const honoursSubHeading = honours.sub_heading || 'Honours & Trophies';
  const honoursHeading = honours.heading || 'School Accolades & Leadership Awards';
  const honoursDescription =
    honours.description ||
    'Celebrating prestigious recognitions, leadership milestones, and distinctions earned by our institution and educators.';

  // Filter awards by category
  const filteredAwards = useMemo(() => {
    if (selectedCategory === 'All') return awards;
    return awards.filter((aw) => {
      const catName = aw.category?.name || '';
      const catSlug = aw.category?.slug || '';
      return (
        catName.toLowerCase() === selectedCategory.toLowerCase() ||
        catSlug.toLowerCase() === selectedCategory.toLowerCase()
      );
    });
  }, [awards, selectedCategory]);

  // Modal navigation helpers
  const openModal = (index) => {
    setActiveModalIndex(index);
  };

  const closeModal = () => {
    setActiveModalIndex(null);
  };

  const nextAward = useCallback(() => {
    if (activeModalIndex === null) return;
    setActiveModalIndex((prev) => (prev + 1) % filteredAwards.length);
  }, [activeModalIndex, filteredAwards.length]);

  const prevAward = useCallback(() => {
    if (activeModalIndex === null) return;
    setActiveModalIndex((prev) => (prev - 1 + filteredAwards.length) % filteredAwards.length);
  }, [activeModalIndex, filteredAwards.length]);

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeModalIndex === null) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') nextAward();
      if (e.key === 'ArrowLeft') prevAward();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeModalIndex, nextAward, prevAward]);

  const activeAward = activeModalIndex !== null ? filteredAwards[activeModalIndex] : null;

  return (
    <>
      <SEO
        slug="recognition"
        title={pageData.title || 'Recognition & Awards'}
        description="Awards, honours, and institutional accreditations of St. Andrews World School, Sonipat."
        keywords="Recognition, Awards, Accreditations, Honours, SAWS Sonipat, Principal Awards"
      />

      {/* ─── PAGE BANNER ─────────────────────────────────────────── */}
      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        image={bannerImgSrc}
        fallback="/images/page-banners/why-choose-us.jpg"
      />

      {/* ─── HONOURS & TROPHIES SECTION (ODD: bg-white — 75% width on desktop) ─ */}
      <section className="py-14 md:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="text-center w-full lg:w-[75%] lg:max-w-[75%] mx-auto mb-10">
              {honoursSubHeading && (
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-2 block">
                  {honoursSubHeading}
                </span>
              )}
              {honoursHeading && (
                <h2 className="text-3xl sm:text-4xl lg:text-[40px] font-bold text-[#1b3160] font-serif mb-3 leading-tight">
                  {honoursHeading}
                </h2>
              )}
              <div className="w-16 h-1 bg-[#d8a85f] mx-auto mb-4 rounded-full" />
              {honoursDescription && (
                <p className="text-gray-600 text-[15px] sm:text-[16px] leading-relaxed">
                  {honoursDescription}
                </p>
              )}
            </div>
          </Reveal>

          {/* Category Filter Pills */}
          {categories.length > 0 && (
            <Reveal delayMs={50}>
              <div className="flex flex-wrap items-center justify-center gap-2.5 mb-12">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('All')}
                  className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 border ${
                    selectedCategory === 'All'
                      ? 'bg-[#1b3160] text-white border-[#1b3160] shadow-md'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#1b3160] hover:text-[#1b3160]'
                  }`}
                >
                  All Honours ({awards.length})
                </button>
                {categories.map((cat) => {
                  const catAwardsCount = awards.filter(
                    (a) => a.category_id === cat.id || a.category?.id === cat.id
                  ).length;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedCategory(cat.name)}
                      className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 border ${
                        selectedCategory === cat.name
                          ? 'bg-[#1b3160] text-white border-[#1b3160] shadow-md'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-[#1b3160] hover:text-[#1b3160]'
                      }`}
                    >
                      {cat.name} ({catAwardsCount})
                    </button>
                  );
                })}
              </div>
            </Reveal>
          )}

          {/* Awards Grid */}
          {loadingAwards ? (
            <div className="py-20 text-center">
              <div className="w-10 h-10 border-4 border-[#1b3160] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-500 text-sm font-medium">Loading awards gallery...</p>
            </div>
          ) : filteredAwards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
              {filteredAwards.map((aw, idx) => (
                <Reveal key={aw.id || idx} delayMs={idx * 50} className="h-full">
                  <div
                    onClick={() => openModal(idx)}
                    className="group cursor-pointer h-full bg-white rounded-none border border-gray-200 hover:border-[#d8a85f] hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between overflow-hidden"
                  >
                    <div>
                      {/* Image Preview with Hover Zoom & Icon Overlay */}
                      <div className="h-72 sm:h-80 md:h-[350px] lg:h-[380px] bg-gray-100 overflow-hidden relative border-b border-gray-100">
                        {aw.image ? (
                          <>
                            <img
                              src={resolveImage(aw.image)}
                              alt={aw.name || 'Award'}
                              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = '/images/page-banners/why-choose-us.jpg';
                              }}
                            />
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-[#1b3160]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <span className="px-4 py-2 rounded-full bg-white text-[#1b3160] flex items-center gap-2 shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 text-xs font-bold uppercase tracking-wider">
                                <i className="fas fa-magnifying-glass-plus text-[#d8a85f]"></i>
                                <span>View Certificate</span>
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full bg-[#f9f8f4] flex flex-col items-center justify-center text-gray-400">
                            <i className="fas fa-trophy text-4xl text-[#d8a85f] mb-2"></i>
                            <span className="text-xs uppercase tracking-wider font-semibold">Honour</span>
                          </div>
                        )}
                      </div>

                      {/* Content Card Body */}
                      <div className="p-5 text-left">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-[#d8a85f] truncate">
                            {aw.category?.name || 'Recognition'}
                          </span>
                          {aw.year && (
                            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-[#1b3160]/10 text-[#1b3160] shrink-0">
                              {aw.year}
                            </span>
                          )}
                        </div>

                        <h3 className="font-serif font-bold text-lg md:text-xl text-[#1b3160] leading-snug mb-2 group-hover:text-[#d8a85f] transition-colors line-clamp-2">
                          {aw.name}
                        </h3>

                        {aw.awarded_by && (
                          <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5 line-clamp-1">
                            <i className="fas fa-award text-[#d8a85f] text-xs"></i>
                            <span>{aw.awarded_by}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Card Footer Link */}
                    <div className="px-5 pb-5 pt-0">
                      <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#1b3160] group-hover:text-[#d8a85f] transition-colors">
                        <span>View Certificate</span>
                        <i className="fas fa-arrow-right text-[11px] transform group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 p-12 text-center max-w-md mx-auto shadow-sm">
              <div className="w-16 h-16 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center mx-auto text-2xl mb-4">
                <i className="fas fa-award" />
              </div>
              <h3 className="font-serif font-bold text-xl text-[#1b3160] mb-2">
                No Awards in This Category
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                There are currently no honours listed under "{selectedCategory}".
              </p>
              <button
                type="button"
                onClick={() => setSelectedCategory('All')}
                className="px-5 py-2 bg-[#1b3160] text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#d8a85f] transition-colors"
              >
                View All Awards
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ─── INTERACTIVE MODAL / LIGHTBOX ────────────────────────── */}
      {activeAward && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-6 animate-in fade-in duration-200"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white max-w-4xl lg:max-w-5xl w-full overflow-hidden shadow-2xl relative flex flex-col max-h-[95vh]"
          >
            {/* Modal Header */}
            <div className="px-6 py-4 bg-[#1b3160] text-white flex items-center justify-between">
              <div className="min-w-0 pr-4">
                <span className="text-[#d8a85f] text-xs font-bold uppercase tracking-widest block mb-0.5">
                  {activeAward.category?.name || 'School Award'}
                </span>
                <h3 className="font-serif text-lg md:text-xl font-bold truncate">
                  {activeAward.name}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors shrink-0"
                aria-label="Close modal"
              >
                <i className="fas fa-times text-sm"></i>
              </button>
            </div>

            {/* Modal Body / Image Viewer */}
            <div className="p-4 md:p-6 overflow-y-auto flex-1 flex flex-col items-center justify-center bg-gray-50">
              {activeAward.image ? (
                <div className="max-h-[75vh] md:max-h-[82vh] flex items-center justify-center overflow-hidden border border-gray-200 bg-white shadow-sm">
                  <img
                    src={resolveImage(activeAward.image)}
                    alt={activeAward.name}
                    className="max-h-[75vh] md:max-h-[82vh] max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="py-16 text-center text-gray-400">
                  <i className="fas fa-trophy text-6xl text-[#d8a85f] mb-3"></i>
                  <p className="font-medium text-gray-600">Honour Certificate</p>
                </div>
              )}

              {/* Award Details Strip */}
              <div className="w-full mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
                {activeAward.awarded_by && (
                  <div className="flex items-center gap-1.5 font-medium">
                    <span className="text-gray-400">Conferred by:</span>
                    <span className="font-bold text-[#1b3160]">{activeAward.awarded_by}</span>
                  </div>
                )}
                {activeAward.year && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">Year:</span>
                    <span className="px-2 py-0.5 rounded bg-[#1b3160]/10 font-bold text-[#1b3160]">
                      {activeAward.year}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer / Navigation Controls */}
            {filteredAwards.length > 1 && (
              <div className="px-6 py-3 bg-white border-t border-gray-100 flex items-center justify-between text-xs font-semibold text-gray-600">
                <button
                  type="button"
                  onClick={prevAward}
                  className="px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <i className="fas fa-chevron-left text-[10px]"></i>
                  <span>Previous</span>
                </button>
                <span className="text-gray-400">
                  {activeModalIndex + 1} of {filteredAwards.length}
                </span>
                <button
                  type="button"
                  onClick={nextAward}
                  className="px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <span>Next</span>
                  <i className="fas fa-chevron-right text-[10px]"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Recognition;
